/**
 * Vorschau-Rechner für Tabellenformeln.
 * Dient ausschließlich der Anzeige im Editor – die verbindlichen Werte
 * berechnet der Server (backend/src/services/formula.ts) exakt in Dezimalarithmetik.
 */
export type PreviewCells = Record<string, string | number | null | undefined>;

const FUNCTIONS = ['SUM', 'AVERAGE', 'MIN', 'MAX', 'COUNT'] as const;

function numberOf(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : NaN;
}

/** Wertet eine einzelne Formel aus; `null`, wenn sie (noch) nicht rechenbar ist. */
export function previewFormula(formula: string, cells: PreviewCells, depth = 0): number | null {
  if (depth > 8) return null;
  const source = formula.trim().replace(/^=/, '');
  if (!source) return null;
  const resolve = (ref: string): number => {
    const raw = cells[ref.toUpperCase()];
    if (typeof raw === 'string' && raw.trim().startsWith('=')) {
      const nested = previewFormula(raw, cells, depth + 1);
      return nested === null ? NaN : nested;
    }
    return numberOf(raw);
  };
  const columnValues = (column: string) => Object.keys(cells)
    .filter(key => new RegExp(`^${column}\\d+$`, 'i').test(key))
    .sort((a, b) => Number(a.replace(/\D/g, '')) - Number(b.replace(/\D/g, '')))
    .map(resolve);
  const rangeValues = (start: string, end: string) => {
    const from = start.match(/^([A-Z]+)(\d+)$/i);
    const to = end.match(/^([A-Z]+)(\d+)$/i);
    if (!from || !to || from[1].toUpperCase() !== to[1].toUpperCase()) return [NaN];
    const values: number[] = [];
    for (let row = Number(from[2]); row <= Number(to[2]); row++) values.push(resolve(`${from[1]}${row}`));
    return values;
  };

  const expanded = source.replace(new RegExp(`\\b(${FUNCTIONS.join('|')})\\(([^()]*)\\)`, 'gi'), (_all, name: string, argument: string) => {
    const trimmed = argument.trim();
    let values: number[];
    if (/^[A-Z]+\d+\s*:\s*[A-Z]+\d+$/i.test(trimmed)) {
      const [start, end] = trimmed.split(':');
      values = rangeValues(start.trim(), end.trim());
    } else if (/^[A-Z]+$/i.test(trimmed)) values = columnValues(trimmed);
    else if (/^[A-Z]+\d+$/i.test(trimmed)) values = [resolve(trimmed)];
    else return 'NaN';
    if (values.some(value => Number.isNaN(value))) return 'NaN';
    const upper = name.toUpperCase();
    if (upper === 'SUM') return String(values.reduce((total, value) => total + value, 0));
    if (upper === 'AVERAGE') return String(values.length ? values.reduce((total, value) => total + value, 0) / values.length : 0);
    if (upper === 'MIN') return String(values.length ? Math.min(...values) : 0);
    if (upper === 'MAX') return String(values.length ? Math.max(...values) : 0);
    return String(values.length);
  });

  const substituted = expanded.replace(/\b[A-Z]+\d+\b/gi, reference => String(resolve(reference)));
  if (!/^[-+*/().\d\s]|NaN/.test(substituted) || /[A-Za-z]/.test(substituted.replace(/NaN/g, ''))) return null;
  if (substituted.includes('NaN')) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict";return (${substituted});`)();
    return Number.isFinite(result) ? Number(result) : null;
  } catch {
    return null;
  }
}

export function formatCell(value: string | number | null | undefined, valueType?: string, decimals?: number): string {
  const raw = value ?? '';
  if (typeof raw === 'string' && raw.trim().startsWith('=')) return raw;
  if (raw === '') return '';
  const parsed = numberOf(raw);
  if (Number.isNaN(parsed)) return String(raw);
  const digits = decimals ?? (valueType === 'CURRENCY' ? 2 : valueType === 'PERCENT' ? 1 : undefined);
  if (valueType === 'CURRENCY') return `${parsed.toLocaleString('de-AT', { minimumFractionDigits: digits ?? 2, maximumFractionDigits: digits ?? 2 })} €`;
  if (valueType === 'PERCENT') return `${parsed.toLocaleString('de-AT', { minimumFractionDigits: digits ?? 1, maximumFractionDigits: digits ?? 1 })} %`;
  if (valueType === 'NUMBER' && digits !== undefined) return parsed.toLocaleString('de-AT', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return String(raw);
}
